// src/utils/splashScreenManager.ts
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

const SPLASH_TIMEOUT_MS = 5000; // 5 seconds

class SplashScreenManager {
  private initCompleted = false;
  private pageLoaded = false;
  private splashHidden = false;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private showSpinnerCallback: (() => void) | null = null;

  public setInitCompleted(): void {
    this.initCompleted = true;
    this.startTimeoutIfNeeded();
    this.checkAndHideSplash();
  }

  public setPageLoaded(): void {
    this.pageLoaded = true;
    this.clearTimeout();
    this.checkAndHideSplash();
  }

  // Keep backwards compatibility
  public setDashboardLoaded(): void {
    this.setPageLoaded();
  }

  public setShowSpinnerCallback(callback: () => void): void {
    this.showSpinnerCallback = callback;
  }

  private startTimeoutIfNeeded(): void {
    // Only start timeout on native platforms and if splash hasn't been hidden yet
    if (
      Capacitor.isNativePlatform() &&
      !this.splashHidden &&
      !this.timeoutTimer
    ) {
      this.timeoutTimer = setTimeout(() => {
        if (!this.pageLoaded && !this.splashHidden) {
          console.log(
            "Splash screen timeout reached, hiding splash and showing spinner"
          );
          this.hideSplashAndShowSpinner();
        }
      }, SPLASH_TIMEOUT_MS);
    }
  }

  private async hideSplashAndShowSpinner(): Promise<void> {
    if (!this.splashHidden && Capacitor.isNativePlatform()) {
      this.splashHidden = true;
      try {
        await SplashScreen.hide();
        // Show spinner overlay if callback is set and we're still waiting for page to load
        if (this.showSpinnerCallback && !this.pageLoaded) {
          this.showSpinnerCallback();
        }
        console.log("Splash screen hidden due to timeout, spinner shown");
      } catch (error) {
        console.warn("Failed to hide splash screen:", error);
      }
    }
  }

  private async checkAndHideSplash(): Promise<void> {
    // Hide splash screen when both init and any page loading are complete
    // and we're on a native platform and splash hasn't been hidden yet
    if (
      this.initCompleted &&
      this.pageLoaded &&
      !this.splashHidden &&
      Capacitor.isNativePlatform()
    ) {
      this.splashHidden = true;
      try {
        await SplashScreen.hide();
        console.log("Splash screen hidden after page loaded");
      } catch (error) {
        console.warn("Failed to hide splash screen:", error);
      }
    }
  }

  private clearTimeout(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  public reset(): void {
    this.initCompleted = false;
    this.pageLoaded = false;
    this.splashHidden = false;
    this.clearTimeout();
    this.showSpinnerCallback = null;
  }
}

export const splashScreenManager = new SplashScreenManager();
